import React from "react";
import {
  Modal,
  Descriptions,
  Steps,
  Table,
  Alert,
  Tag,
  Card,
  Row,
  Col,
  Divider,
  Typography,
  Space,
} from "antd";
import {
  ToolOutlined,
  WarningOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  SettingOutlined,
} from "@ant-design/icons";

const { Step } = Steps;
const { Text, Title } = Typography;

/**
 * Full-view / printable modal for a single work instruction.
 * Renders corrugation, sheet cutting, pasting, and generic stages.
 */
const WorkInstructionDetail = ({ instruction, onClose }) => {
  const { content, materials } = instruction;

  // ── Render helpers ─────────────────────────────────────────

  const renderHeader = () => {
    if (!content?.header) return null;
    const h = content.header;
    const showSheets = h.sheet_quantity && h.sheets_per_box > 1 && content.output_items;
    return (
      <Descriptions bordered size="small" column={showSheets ? 4 : 3} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Job #">{h.job_number}</Descriptions.Item>
        <Descriptions.Item label="Item">{h.item_name}</Descriptions.Item>
        <Descriptions.Item label="Boxes">
          {(h.box_quantity || h.quantity || 0).toLocaleString()}
        </Descriptions.Item>
        {showSheets && (
          <Descriptions.Item label="Sheets">
            {h.sheet_quantity.toLocaleString()} <Text type="secondary">(N={h.sheets_per_box})</Text>
          </Descriptions.Item>
        )}
        {h.ply && <Descriptions.Item label="Ply">{h.ply}</Descriptions.Item>}
        {h.customer_id && (
          <Descriptions.Item label="Customer">{h.customer_id}</Descriptions.Item>
        )}
      </Descriptions>
    );
  };

  const renderAssignedMachine = () => {
    if (!content?.assigned_machine) return null;
    const m = content.assigned_machine;
    return (
      <div style={{ marginBottom: 16 }}>
        <Text type="secondary">Machine: </Text>
        <Tag color="blue">{m.machine_code} ({m.machine_name})</Tag>
        {m.machine_subtype && (
          <Tag color="geekblue">{m.machine_subtype.replace(/_/g, " ").toUpperCase()}</Tag>
        )}
        {m.is_outsourced && <Tag color="orange">Outsourced — {m.vendor_name}</Tag>}
      </div>
    );
  };

  const renderOutput = () => {
    if (!content?.output) return null;
    const o = content.output;
    
    // Pasting / Casting output - show as bold table (sheets)
    if (o.sheet_count) {
      return (
        <Card
          title={<><InfoCircleOutlined /> Output</>}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={[{ key: 1, output: o.type, sheets: o.sheet_count }]}
            pagination={false}
            size="small"
            columns={[
              {
                title: "Output",
                dataIndex: "output",
                render: (val) => <Text strong style={{ fontSize: 16 }}>{val}</Text>,
              },
              {
                title: "Quantity",
                dataIndex: "sheets",
                render: (val) => <Text strong style={{ fontSize: 16 }}>{val?.toLocaleString()} sheets</Text>,
              },
            ]}
          />
        </Card>
      );
    }
    
    // Joining output - boxes with joins count
    if (o.box_count && o.joins_per_box) {
      return (
        <Card
          title={<><InfoCircleOutlined /> Output</>}
          size="small"
          style={{ marginBottom: 16 }}
        >
          <Table
            dataSource={[{ key: 1, output: o.type, boxes: o.box_count, joins: o.joins_per_box }]}
            pagination={false}
            size="small"
            columns={[
              {
                title: "Output",
                dataIndex: "output",
                render: (val) => <Text strong style={{ fontSize: 16 }}>{val}</Text>,
              },
              {
                title: "Qty of Boxes",
                dataIndex: "boxes",
                render: (val) => <Text strong style={{ fontSize: 16 }}>{val?.toLocaleString()}</Text>,
              },
              {
                title: "Joins per Box (N)",
                dataIndex: "joins",
                render: (val) => <Text strong style={{ fontSize: 16 }}>{val}</Text>,
              },
            ]}
          />
        </Card>
      );
    }
    
    // Other outputs - text format
    return (
      <Card
        title={<><InfoCircleOutlined /> Expected Output</>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Text strong>{o.type}</Text>
        {o.description && <Text> — {o.description}</Text>}
        {o.roll_width && !o.roll_size && (
          <Text> — Roll Width: {o.roll_width}"</Text>
        )}
        {o.roll_size && <Text> — RS: {o.roll_size}" × CS: {o.cutting_size}"</Text>}
      </Card>
    );
  };

  // Corrugation-specific: single combined output card
  const renderCorrugationOutput = () => {
    if (!content?.corrugation_output) return null;
    const co = content.corrugation_output;
    return (
      <Card title="📦 2-Ply Sheet Output" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Text type="secondary">Liner</Text><br />
            <Text strong>{co.liner.material} {co.liner.gsm} GSM</Text><br />
            <Text>{co.liner.total_weight_kg} kg</Text>
          </Col>
          <Col span={6}>
            <Text type="secondary">Flute</Text><br />
            <Text strong>{co.flute.material} {co.flute.gsm} GSM</Text><br />
            <Text>FF: {co.flute.ff} — {co.flute.total_weight_kg} kg</Text>
          </Col>
          <Col span={6}>
            <Text type="secondary">Net Weight</Text><br />
            <Text strong style={{ fontSize: 16 }}>{co.net_weight_kg} kg</Text>
          </Col>
          <Col span={6}>
            <Text type="secondary">Roll Width</Text><br />
            <Text strong>{content.output?.roll_width}"</Text>
          </Col>
        </Row>
      </Card>
    );
  };

  // Sheet cutter: output items table
  const renderOutputItems = () => {
    if (!content?.output_items || content.output_items.length === 0) return null;
    return (
      <Card title="📦 Output Needed" size="small" style={{ marginBottom: 16 }}>
        <Table
          dataSource={content.output_items}
          pagination={false}
          size="small"
          rowKey={(_, i) => i}
          columns={[
            { title: "#", width: 40, render: (_, __, idx) => idx + 1 },
            {
              title: "Item",
              render: (_, item) => (
                <span>
                  <strong>{item.label}</strong>
                  {item.material && <Text type="secondary"> ({item.material})</Text>}
                </span>
              ),
            },
            { title: "Size", dataIndex: "size" },
            { title: "Quantity", render: (_, item) => <strong>{item.quantity?.toLocaleString()}</strong> },
            { title: "Weight", render: (_, item) => <strong>{item.weight_kg} kg</strong> },
            {
              title: "Stage",
              render: (_, item) => item.handled_at ? (
                <Tag color={item.handled_at === "SHEET_CUTTING" ? "green" : "orange"}>
                  {item.handled_at === "SHEET_CUTTING" ? "Here" : "→ Pasting"}
                </Tag>
              ) : <Tag color="green">Here</Tag>,
            },
          ]}
        />
      </Card>
    );
  };

  // Sheet cutter: cutting specs
  const renderCuttingSpecs = () => {
    if (!content?.cutting_specs) return null;
    return (
      <Card title="✂️ Cutting Specs" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={12}>
            <Text type="secondary">Roll Size (RS):</Text>{" "}
            <Text strong>{content.cutting_specs.roll_size}"</Text>
          </Col>
          <Col span={12}>
            <Text type="secondary">Cutting Size (CS):</Text>{" "}
            <Text strong>{content.cutting_specs.cutting_size}"</Text>
          </Col>
        </Row>
      </Card>
    );
  };

  // Sheet cutter: weight summary
  const renderWeightSummary = () => {
    if (!content?.weight_summary) return null;
    const ws = content.weight_summary;
    return (
      <Card title="⚖️ Weight Summary" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Text type="secondary">2-Ply Sheets ({ws.two_ply_sheet_qty?.toLocaleString()})</Text><br />
            <Text strong>{ws.two_ply_sheet_weight_kg} kg</Text>
          </Col>
          <Col span={6}>
            <Text type="secondary">Top Paper ({ws.top_paper_qty?.toLocaleString()})</Text><br />
            <Text strong>{ws.top_paper_weight_kg} kg</Text>
          </Col>
          <Col span={6}>
            <Text type="secondary">Total Weight</Text><br />
            <Text strong>{ws.total_weight_kg} kg</Text>
          </Col>
          <Col span={6}>
            <Text type="secondary">Expected (Box Wt × Qty)</Text><br />
            <Text strong>{ws.expected_net_weight_kg} kg</Text>
            {ws.box_weight_kg > 0 && (
              <Text type="secondary"> ({ws.box_weight_kg} kg × {content.header?.box_quantity?.toLocaleString()})</Text>
            )}
          </Col>
        </Row>
        <div style={{ marginTop: 8 }}>
          <Tag color={ws.verification === "✅ MATCH" ? "green" : "warning"}>{ws.verification}</Tag>
          {ws.weight_difference_kg !== 0 && (
            <Text type="secondary"> Difference: {ws.weight_difference_kg > 0 ? "+" : ""}{ws.weight_difference_kg} kg</Text>
          )}
        </div>
      </Card>
    );
  };

  // Pasting passes
  const renderPastingPasses = () => {
    if (!content?.pasting_passes || content.pasting_passes.length === 0) return null;
    return (
      <Card title="🔄 Pasting Passes" size="small" style={{ marginBottom: 16 }}>
        {content.pasting_passes.map((pass) => (
          <div key={pass.pass} style={{ marginBottom: 8, padding: "8px 12px", background: "#f6ffed", border: "1px solid #b7eb8f", borderRadius: 4 }}>
            <Text strong>Pass {pass.pass}: </Text>
            <Text>{pass.description}</Text>
            <div style={{ marginTop: 4 }}>
              <Text type="secondary">Input: </Text>
              {pass.input.map((inp, i) => <Tag key={i} color="blue">{inp}</Tag>)}
              <Text type="secondary"> → Output: </Text>
              <Tag color="green">{pass.output}</Tag>
            </div>
          </div>
        ))}
      </Card>
    );
  };

  // Top paper (pasting / sheet cutter)
  const renderTopPaper = () => {
    if (!content?.top_paper) return null;
    const tp = content.top_paper;
    return (
      <Card title="📄 Top Paper" size="small" style={{ marginBottom: 16, background: "#f6ffed" }}>
        <Row gutter={16}>
          <Col span={8}>
            <Text type="secondary">Material:</Text>{" "}
            <Text strong>{tp.material} {tp.gsm} GSM</Text>
            {tp.bf && <Text> (BF: {tp.bf})</Text>}
          </Col>
          <Col span={8}>
            <Text type="secondary">Weight:</Text>{" "}
            <Text strong>{tp.weight_kg} kg</Text>
          </Col>
          <Col span={8}>
            <Text type="secondary">Sheets:</Text>{" "}
            <Text strong>{tp.sheets?.toLocaleString()}</Text>
          </Col>
        </Row>
      </Card>
    );
  };

  // Top layer note (corrugation)
  const renderTopLayerNote = () => {
    if (!content?.top_layer_note) return null;
    return (
      <Alert
        type="warning"
        message={content.top_layer_note.message}
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  };

  // Planning notes
  const renderPlanningNotes = () => {
    if (!content?.planning_notes || content.planning_notes.length === 0) return null;
    return (
      <Alert
        type="info"
        message="Planning Notes"
        description={
          <ul style={{ marginBottom: 0 }}>
            {content.planning_notes.map((note, i) => <li key={i}>{note}</li>)}
          </ul>
        }
        showIcon
        style={{ marginBottom: 16 }}
      />
    );
  };

  const renderGum = () => {
    if (!content?.gum) return null;
    return (
      <Card title="🧴 Adhesive / Gum" size="small" style={{ marginBottom: 16 }}>
        <Descriptions column={2} size="small">
          <Descriptions.Item label="Type">{content.gum.type}</Descriptions.Item>
          <Descriptions.Item label="Estimated Qty">
            {content.gum.estimated_kg} kg
          </Descriptions.Item>
        </Descriptions>
      </Card>
    );
  };

  const renderMachineSettings = () => {
    if (!content?.machine_settings) return null;
    const settings = content.machine_settings;
    return (
      <Card
        title={<><SettingOutlined /> Machine Settings</>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Row gutter={[16, 8]}>
          {Object.entries(settings).map(([key, value]) => (
            <Col span={8} key={key}>
              <div style={{ fontSize: 12, color: "#888" }}>
                {key.replace(/_/g, " ").toUpperCase()}
              </div>
              <div style={{ fontSize: 16, fontWeight: 500 }}>
                {typeof value === "number" ? value.toLocaleString() : String(value)}
              </div>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  const renderMaterials = () => {
    const mats = materials || content?.materials;
    if (!mats || mats.length === 0) return null;

    const columns = [
      { title: "Category", dataIndex: "category", key: "category" },
      { title: "Material", dataIndex: "name", key: "name" },
      {
        title: "Quantity",
        dataIndex: "quantity",
        key: "quantity",
        render: (v) => (typeof v === "number" ? v.toLocaleString() : v),
      },
      { title: "Unit", dataIndex: "unit", key: "unit" },
    ];

    return (
      <Card
        title={<><ToolOutlined /> Materials Required</>}
        size="small"
        style={{ marginBottom: 16 }}
      >
        <Table
          columns={columns}
          dataSource={mats}
          rowKey={(r, i) => i}
          pagination={false}
          size="small"
        />
      </Card>
    );
  };

  // Layer configuration
  const renderLayerConfiguration = () => {
    if (!content?.layer_configuration || content.layer_configuration.length === 0) return null;
    return (
      <Card title="📐 Layer Configuration" size="small" style={{ marginBottom: 16 }}>
        <Space size={4} wrap>
          {content.layer_configuration.map((l, idx) => (
            <Tag key={idx} color={l.note?.includes("PASTE") ? "orange" : (l.processed_in === "PASTING" ? "orange" : "green")}>
              L{l.layer}: {l.material} {l.gsm} GSM ({l.position || l.type})
              {l.ff ? ` FF:${l.ff}` : ""}
              {l.note || (l.processed_in === "PASTING" ? " → Pasting" : "")}
            </Tag>
          ))}
        </Space>
      </Card>
    );
  };

  const renderSteps = () => {
    if (!content?.steps || content.steps.length === 0) return null;
    return (
      <Card title="Process Steps" size="small" style={{ marginBottom: 16 }}>
        <Steps direction="vertical" size="small">
          {content.steps.map((step, index) => (
            <Step
              key={index}
              title={step.title || `Step ${index + 1}`}
              description={step.description}
              status="process"
            />
          ))}
        </Steps>
      </Card>
    );
  };

  const renderQualityChecks = () => {
    if (!content?.quality_checks || content.quality_checks.length === 0) return null;
    return (
      <Card title={<><CheckCircleOutlined /> Quality Checks</>} size="small" style={{ marginBottom: 16 }}>
        <ul>
          {content.quality_checks.map((check, index) => (
            <li key={index}>
              <strong>{check.checkpoint}:</strong> {check.specification}
              {check.critical && <Tag color="red" style={{ marginLeft: 8 }}>Critical</Tag>}
            </li>
          ))}
        </ul>
      </Card>
    );
  };

  const renderSafetyNotes = () => {
    if (!content?.safety_notes || content.safety_notes.length === 0) return null;
    return (
      <Alert
        type="warning"
        message="Safety Notes"
        description={
          <ul style={{ marginBottom: 0 }}>
            {content.safety_notes.map((note, i) => <li key={i}>{note}</li>)}
          </ul>
        }
        showIcon
        icon={<WarningOutlined />}
        style={{ marginBottom: 16 }}
      />
    );
  };

  const renderOperatorFields = () => {
    if (!content?.operator_fields) return null;
    return (
      <Card title="Operator Sign-Off" size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          {Object.entries(content.operator_fields).map(([key, value]) => (
            <Col span={8} key={key}>
              <Text type="secondary">{key.replace(/_/g, " ")}:</Text>{" "}
              <Text strong>{value || "___"}</Text>
            </Col>
          ))}
        </Row>
      </Card>
    );
  };

  // Notes
  const renderNotes = () => {
    if (!instruction.notes) return null;
    return (
      <Card size="small" style={{ marginBottom: 16 }}>
        <Text type="secondary">Notes:</Text> <Text>{instruction.notes}</Text>
      </Card>
    );
  };

  return (
    <Modal
      title={`Work Instruction: ${instruction.stage_name}`}
      open={true}
      onCancel={onClose}
      width={900}
      footer={null}
    >
      {/* Top-level instruction meta */}
      <Descriptions bordered size="small" column={3} style={{ marginBottom: 16 }}>
        <Descriptions.Item label="Job #">{instruction.job_number}</Descriptions.Item>
        <Descriptions.Item label="Stage">{instruction.stage_name}</Descriptions.Item>
        <Descriptions.Item label="Status">
          <Tag color={instruction.status === "COMPLETED" ? "green" : "blue"}>{instruction.status}</Tag>
        </Descriptions.Item>
        <Descriptions.Item label="Type">{instruction.instruction_type}</Descriptions.Item>
        <Descriptions.Item label="QR Code">
          <Text copyable style={{ fontSize: 12 }}>{instruction.qr_code}</Text>
        </Descriptions.Item>
        <Descriptions.Item label="Generated">
          {instruction.created_at ? new Date(instruction.created_at).toLocaleString() : "-"}
        </Descriptions.Item>
      </Descriptions>

      {/* Safety Notes */}
      {renderSafetyNotes()}

      {/* Header */}
      {renderHeader()}

      {/* Assigned Machine */}
      {renderAssignedMachine()}

      {/* Expected Output */}
      {renderOutput()}

      {/* Corrugation: 2-ply sheet output */}
      {renderCorrugationOutput()}

      {/* Top Layer Note */}
      {renderTopLayerNote()}

      {/* Sheet Cutter: Output Items */}
      {renderOutputItems()}

      {/* Sheet Cutter: Cutting Specs */}
      {renderCuttingSpecs()}

      {/* Sheet Cutter: Weight Summary */}
      {renderWeightSummary()}

      {/* Pasting: Passes */}
      {/* Pasting Passes - hidden for now, data still calculated in backend */}
      {/* {renderPastingPasses()} */}

      {/* Top Paper - hide for pasting (output already shown above) */}
      {!content?.output?.sheet_count && renderTopPaper()}

      {/* Layer Configuration - hide for pasting */}
      {!content?.output?.sheet_count && renderLayerConfiguration()}

      {/* Planning Notes */}
      {renderPlanningNotes()}

      {/* Gum */}
      {renderGum()}

      {/* Machine Settings */}
      {renderMachineSettings()}

      {/* Materials */}
      {renderMaterials()}

      {/* Process Steps */}
      {renderSteps()}

      {/* Quality Checks */}
      {renderQualityChecks()}

      {/* Operator Fields */}
      {renderOperatorFields()}

      {/* Notes */}
      {renderNotes()}

      {/* Completion */}
      {instruction.status === "COMPLETED" && (
        <Card size="small" style={{ marginBottom: 16, background: "#f6ffed" }}>
          <Descriptions column={3} size="small">
            <Descriptions.Item label="Completed At">
              {instruction.completed_at ? new Date(instruction.completed_at).toLocaleString() : "-"}
            </Descriptions.Item>
            <Descriptions.Item label="Completed By">
              {instruction.completed_by_name || "-"}
            </Descriptions.Item>
          </Descriptions>
        </Card>
      )}
    </Modal>
  );
};

export default WorkInstructionDetail;
